import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { getRachelDashboard, requestInitiatePayment } from '@renter-hub/actions/requests';
import { ScheduleMoveInCostsWebApiHandler_scheduleMoveInCosts } from '@zg-rentals/ts-rent-payment-client';
import { closeNotification, showModalNotification, showPageNotification } from '@common/actions/NotificationActions';
import { checkRequestActionSuccess, getSignInPage } from '@common/paymentsUtils/api';
import TransactionConstants from '@common/constants/TransactionConstants';
import {
  DEFAULT_ERROR,
  PAYMENT_METHOD_UNPROCESSABLE,
  PAYMENT_METHOD_UPDATED_SUCCESS,
} from '@renter-hub/constants/notifications';
import HTTP_STATUS from '@common/constants/HttpConstants';
import { ErrorInterface } from '@common/types';
import { RENT_PAYMENT } from '@renter-hub/constants/paymentConstants';
import paths from '@renter-hub/pathsManifest';

import UpdatePaymentMethodsPage from './component';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (state, { location }) => {
  return {
    paymentMethods: state.payments.renter.dashboard.paymentMethods,
    paymentSubscriptionDetails: state.payments.renter.dashboard.paymentDetails.paymentSubscriptionDetails,
    moveInCostsDetails: state.payments.renter.dashboard.moveInCostsDetails,
    typeOfPaymentToMake: location?.state?.typeOfPaymentToMake,
    nextPayable: state.payments.renter.dashboard.nextPayable,
    nextScheduleMonth: state.payments.renter.dashboard.nextScheduleMonth,
    newPaymentMethodId: location?.state?.paymentMethodId,
    transactions: state.payments.renter.dashboard.paymentHistory.transactions,
    currentScheduleMonth: state.payments.renter.dashboard.currentScheduleMonth,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId, fromDashboard, location }) => ({
  onAddNewPaymentClick: ({ typeOfPaymentToMake }) => {
    return dispatch(
      push(baseUrl + paths.editPaymentInfoPage, {
        fromWhere: paths.updatePaymentMethodsPage,
        typeOfPaymentToMake,
      }),
    );
  },
  onBack: async () => {
    await dispatch(closeNotification());
    await dispatch(push(baseUrl));
    await dispatch(getRachelDashboard({ propertyId }));
  },
  updatePaymentMethod: async ({
    paymentMethodId,
    nextPayable,
    paymentSubscriptionDetails,
    typeOfPaymentToMake,
    moveInCostsDetails,
    transactions,
    currentScheduleMonth,
    nextScheduleMonth,
  }) => {
    try {
      await dispatch(closeNotification());

      const currentTransaction = transactions.find(
        (transaction) => currentScheduleMonth.scheduleMonth === transaction.scheduleMonth,
      );

      const isTransactionPaid = currentTransaction?.renterStatus === TransactionConstants.STATUS.COMPLETE;

      if (typeOfPaymentToMake === RENT_PAYMENT) {
        const updatePaymentResponse = await dispatch(
          requestInitiatePayment({
            scheduleMonth: isTransactionPaid ? nextScheduleMonth.scheduleMonth : nextPayable.scheduleMonth,
            paymentDate: paymentSubscriptionDetails.nextScheduledDate,
            encodedPaymentMethodId: paymentMethodId,
            recurring: paymentSubscriptionDetails.recurring,
          }),
        );
        await checkRequestActionSuccess(updatePaymentResponse);
      } else {
        // Parse YYYY-MM-DD date to LocalDate
        const [year, month, day] = moveInCostsDetails.sendDate.split('-').map(Number);

        await ScheduleMoveInCostsWebApiHandler_scheduleMoveInCosts({
          encodedPaymentMethodId: paymentMethodId,
          groupKey: moveInCostsDetails.groupKey,
          paymentDate: { day, month, year },
        });
      }
      // Request the latest renter status
      const [renterStatusResponse] = await dispatch(getRachelDashboard({ propertyId }));
      await checkRequestActionSuccess(renterStatusResponse);

      await dispatch(push(baseUrl));

      if (fromDashboard) {
        // Show the account updated success banner
        await dispatch(showPageNotification(PAYMENT_METHOD_UPDATED_SUCCESS));
      }
    } catch (err) {
      // By default, show the default error banner
      let notificationType = DEFAULT_ERROR;
      const error = err as ErrorInterface;
      if (error.action && error.action.payload) {
        const { status } = error.action.payload;
        // If 401 status, redirect to sign-in
        if (status === HTTP_STATUS.SC_UNAUTHORIZED) {
          window.location = getSignInPage();
          return null;
        }
        // If 422 status, show payment method unprocessable banner
        if (status === HTTP_STATUS.SC_UNPROCESSABLE_ENTITY) {
          notificationType = PAYMENT_METHOD_UNPROCESSABLE;
        }
      }

      logError({ error, context: { details: 'updatePaymentMethod', notificationType } });
      dispatch(showModalNotification(notificationType));
      return { error: true };
    }
  },
});

const mergeProps = (stateProps, dispatchProps) => {
  return {
    ...stateProps,
    ...dispatchProps,
    updatePaymentMethod: (paymentMethodId) =>
      dispatchProps.updatePaymentMethod({
        paymentMethodId,
        paymentSubscriptionDetails: stateProps.paymentSubscriptionDetails,
        nextPayable: stateProps.nextPayable,
        moveInCostsDetails: stateProps.moveInCostsDetails,
        typeOfPaymentToMake: stateProps.typeOfPaymentToMake,
        transactions: stateProps.transactions,
        currentScheduleMonth: stateProps.currentScheduleMonth,
        nextScheduleMonth: stateProps.nextScheduleMonth,
      }),
    onAddNewPaymentClick: () =>
      dispatchProps.onAddNewPaymentClick({
        typeOfPaymentToMake: stateProps.typeOfPaymentToMake,
      }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(UpdatePaymentMethodsPage);
